import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { OfficeReviewsService } from '../services';

export const OFFICE_REVIEWS_QUERY_KEY = 'GET_OFFICE_REVIEWS';

/** Matches the endpoint's own default page size. */
export const OFFICE_REVIEWS_PAGE_SIZE = 15;

/**
 * One page of an office's reviews.
 *
 * Page-based rather than an infinite query: organization-service returns
 * `{ reviews, total, limit, page }` (organization-service#139), which maps
 * straight onto `DsPagination`'s `page` / `pageSize` / `totalCount`. GOTU-7338
 * originally scrolled instead, but a stepper is the more legible affordance for
 * a table admins scan — with infinite scroll there was no way to tell how many
 * reviews an office had, and the trigger hung off a scroll listener attached to
 * an ancestor element by id.
 *
 * Replaces the Redux path for office pages. `fetchUserReviews` and the
 * `FETCH_USER_REVIEWS_*` reducer cases are left untouched and simply stop
 * firing for offices — they are **not** deleted, because professional pages
 * still use every one of them until they get their own revamp.
 *
 * `placeholderData: keepPreviousData` keeps the previous page's rows on screen
 * while the next one loads, so stepping does not blank the table and then
 * repaint it. It also means `reviews` stays populated during a *failed* page
 * load, which is what lets the screen show a retry beneath the rows rather than
 * replacing them.
 *
 * @param {Object} params
 * @param {string} params.officeId - The office whose reviews to list
 * @param {number} [params.page=1] - 1-based page index
 * @param {boolean} [params.enabled=true] - False while the profile is still
 *   resolving the office
 */
export const useGetOfficeReviews = ({ officeId, page = 1, enabled = true }) => {
  const { data, error, isError, isLoading, isPlaceholderData, isFetching, refetch } = useQuery({
    queryKey: [OFFICE_REVIEWS_QUERY_KEY, officeId, page],
    queryFn: () =>
      OfficeReviewsService.getOfficeReviews({
        officeId,
        page,
        limit: OFFICE_REVIEWS_PAGE_SIZE,
      }),
    enabled: enabled && Boolean(officeId),
    retry: false,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    // The screen renders its own error state. Without this, `queryClient`'s
    // QueryCache.onError also raises a toast carrying the raw axios message
    // ("Request failed with status code 500"), so one failure reports twice.
    // `createTestQueryClient` installs no caches, so tests never reveal it.
    meta: { skipGlobalErrorToaster: true },
  });

  return {
    reviews: data?.response ?? [],

    // Nullish rather than 0 while unknown: `useClampedPage` treats a nullish
    // total as "don't clamp", which matters on a failed fetch — clamping on a
    // 0 fallback would reset the admin's page to 1 and refetch over the error.
    total: data?.total ?? null,

    page,
    pageSize: OFFICE_REVIEWS_PAGE_SIZE,

    error,
    isError,

    /** The very first load, when there is nothing on screen yet. */
    isInitialLoading: isLoading,

    /**
     * A page change with the previous page still rendered. `isFetching` alone
     * would also be true on the first load; pairing it with
     * `isPlaceholderData` isolates the stepping case.
     */
    isFetchingPage: isFetching && isPlaceholderData,

    refetch,
  };
};
